import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { confirmEmail, resendEmailConfirmation } from '../utils/api';

function ConfirmEmail() {
  const [searchParams] = useSearchParams();

  const email = searchParams.get('email');
  const token = searchParams.get('token');

  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('Confirming your email...');
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  useEffect(() => {
    const run = async () => {
      setResendMessage('');

      if (!email || !token) {
        setStatus('error');
        setMessage('This confirmation link is incomplete. Please request a new confirmation email.');
        return;
      }

      try {
        const response = await confirmEmail({ email, token });

        if (response?.status === false || response?.success === false) {
          setStatus('error');
          setMessage(response?.message || 'This confirmation link is invalid or expired.');
          return;
        }

        setStatus('success');
        setMessage(response?.message || 'Email confirmed successfully. You can now sign in.');
      } catch (err) {
        setStatus('error');

        const backendMessage =
          err?.response?.data?.message ||
          err?.message ||
          'Email confirmation failed.';

        if (
          backendMessage.includes('Resource not found') ||
          backendMessage.includes('404')
        ) {
          setMessage('The confirmation endpoint was not found. Please confirm your backend route is POST /confirm-email.');
        } else {
          setMessage(backendMessage);
        }
      }
    };

    run();
  }, [email, token]);

  const handleResend = async () => {
    if (!email) {
      setResendMessage('Email address is missing from this link.');
      return;
    }

    setResending(true);
    setResendMessage('');

    try {
      const response = await resendEmailConfirmation({ email });

      if (response?.status === false || response?.success === false) {
        setResendMessage(response?.message || 'Could not resend confirmation email.');
        return;
      }

      setResendMessage(response?.message || 'Confirmation email resent successfully.');
    } catch (err) {
      setResendMessage(
        err?.response?.data?.message ||
          err?.message ||
          'Could not resend confirmation email.'
      );
    } finally {
      setResending(false);
    }
  };

  const isLoading = status === 'loading';
  const isSuccess = status === 'success';
  const isError = status === 'error';

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4 py-12">
      <div className="max-w-lg w-full bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
        <div
          className={`h-2 ${
            isSuccess ? 'bg-green-600' : isError ? 'bg-red-600' : 'bg-primary'
          }`}
        />

        <div className="p-8 sm:p-10 text-center">
          <div
            className={`mx-auto mb-6 w-20 h-20 rounded-full flex items-center justify-center text-4xl ${
              isSuccess
                ? 'bg-green-100 text-green-700'
                : isError
                ? 'bg-red-100 text-red-700'
                : 'bg-primary/10 text-primary'
            }`}
          >
            {isLoading ? '…' : isSuccess ? '✓' : '!'}
          </div>

          <h1 className="text-3xl sm:text-4xl font-playfair-display font-semibold text-primary mb-4">
            Email Confirmation
          </h1>

          <p
            className={`text-base sm:text-lg font-montserrat leading-relaxed ${
              isSuccess
                ? 'text-green-700'
                : isError
                ? 'text-red-700'
                : 'text-secondary'
            }`}
          >
            {message}
          </p>

          {email && (
            <p className="mt-4 text-sm text-gray-500 break-all">
              Account email: <span className="font-medium">{email}</span>
            </p>
          )}

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            {isSuccess && (
              <Link
                to="/login"
                className="inline-flex justify-center items-center px-6 py-3 rounded-full bg-primary text-cream font-montserrat font-medium hover:bg-primary/90 transition"
              >
                Go to Login
              </Link>
            )}

            {isError && (
              <>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending || !email}
                  className="inline-flex justify-center items-center px-6 py-3 rounded-full bg-primary text-cream font-montserrat font-medium hover:bg-primary/90 transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {resending ? 'Resending...' : 'Resend Confirmation'}
                </button>

                <Link
                  to="/register"
                  className="inline-flex justify-center items-center px-6 py-3 rounded-full border border-gray-300 text-secondary font-montserrat font-medium hover:bg-gray-50 transition"
                >
                  Register Again
                </Link>
              </>
            )}
          </div>

          {resendMessage && (
            <div className="mt-6 p-4 rounded-xl bg-gray-50 border border-gray-200 text-sm text-secondary">
              {resendMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ConfirmEmail;